import React, { useEffect, useState } from "react";
import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  InputAdornment,
  Chip,
  MenuItem,
  CircularProgress,
  Select,
  FormControl,
  Pagination,
  Box,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import useFetchObjects from "../../../../api/useFetchObjects";
import { useAuth } from "../../../../context";
import Loading from "./Loading";
import ErrorPage from "../../../../Components/GeneralComponents/errorPage/ErrorPage";
import FormatDateTime from "../../../../Components/GeneralComponents/DateFormatter/FormatDateTime";
import useDelete from "../../../../api/useDelete";
import { useTranslation } from "react-i18next";
import EditProcedure from "./EditProcedure";
import { useNavigate } from "react-router-dom";

// Use a smaller cell style for a compact theme
const cellStyle = {
  padding: "6px",
  fontSize: "0.8rem",
  textAlign: "center",
  whiteSpace: "nowrap",
};

const headCellStyle = {
  ...cellStyle,
  fontWeight: "bold",
  backgroundColor: "#f5f5f5",
};

const ProcedureList = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const user = auth?.user;
  const token = user?.token;

  const [procedureId, setProcedureId] = useState(null);
  const [keyword, setKeyword] = useState("");
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState(null);
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [date, setDate] = useState("");

  // State for pagination
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalProcedure, setTotalProcedure] = useState(0);
  const navigate = useNavigate();
  const {
    data: procedures,
    isLoading,
    isError,
    refetch,
  } = useFetchObjects(
    ["procedure", page, status, rowsPerPage, type],
    `procedure/?search=${encodeURIComponent(
      keyword
    )}&type=${type}&status=${status}&procedure_date=${date}&page=${page}&page_size=${rowsPerPage}`,
    token
  );

  const {
    data: typesData,
    isLoading: isLoadingType,
    isError: isErrorType,
  } = useFetchObjects(["procedure-type"], "procedure-type", token);

  const { handleDelete, ConfirmDialog } = useDelete("procedure", token);

  useEffect(() => {
    refetch();
  }, [keyword, type, status, date, refetch, page, rowsPerPage]);

  useEffect(() => {
    if (procedures && procedures.count !== undefined) {
      setTotalProcedure(procedures.count);
    }
  }, [procedures]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  if (isLoading) {
    return <Loading />;
  }

  if (isError) {
    return <ErrorPage />;
  }

  const handleOpenDialog = () => {
    setDescription(null);
    setOpen(!open);
  };

  // Function to get row background color based on procedure date and status.
  // Date-based colors (today/tomorrow) take precedence over status-based colors.
  const getRowBackgroundColor = (procedure) => {
    // Check if the procedure date is today or tomorrow.
    if (procedure.procedure_date) {
      const procedureDate = new Date(procedure.procedure_date);
      const today = new Date();
      const tomorrow = new Date(today);
      today.setHours(0, 0, 0, 0);
      tomorrow.setDate(today.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);
      procedureDate.setHours(0, 0, 0, 0);
      if (procedureDate.getTime() === today.getTime()) {
        return { backgroundColor: "#9ef0a5", color: "white" }; // Today: Green
      } else if (procedureDate.getTime() === tomorrow.getTime()) {
        return { backgroundColor: "#b1b4fa", color: "white" }; // Tomorrow: Blue
      }
    }
    // If no special date condition applies, set color based on status.
    // Status values are assumed numeric.
    const statusMapping = {
      1: { backgroundColor: "#d4edda", color: "#155724" }, // done: Light green
      2: { backgroundColor: "#f8d7da", color: "#721c24" }, // cancel: Light red
      3: { backgroundColor: "#fff3cd", color: "#856404" }, // followUP: Light yellow
      4: { backgroundColor: "#d1ecf1", color: "#0c5460" }, // inProgress: Light blue
      5: { backgroundColor: "#e2e3e5", color: "#41464b" }, // dontWant: Grey
      6: { backgroundColor: "#e2d4f7", color: "#4e3d6a" }, // nextDate: Light purple
      7: { backgroundColor: "#ffeeba", color: "#856404" }, // pending: Light orange
      8: { backgroundColor: "#c3e6cb", color: "#155724" }, // fixed: Green
      9: { backgroundColor: "#d1f0e5", color: "#0c5460" }, // flexible: Light teal
      10: { backgroundColor: "#fdfdfe", color: "black" }, // noAnswer: Almost white
      11: { backgroundColor: "#d4f1f9", color: "#0c5460" }, // information: Light cyan
      12: { backgroundColor: "#fce4ec", color: "#880e4f" }, // comingSoon: Light pink
      13: { backgroundColor: "#ced4da", color: "#495057" }, // block: Dark grey
    };

    const statusValue = procedure.status;
    return (
      statusMapping[statusValue] || { backgroundColor: "white", color: "black" }
    );
  };

  // Group procedures by date (using procedure_date_only)
  const groupedProcedures =
    procedures?.results?.reduce((groups, procedure) => {
      if (!procedure.procedure_date_only) return groups; // Skip procedures with no procedure_date_only
      const dateString = procedure.procedure_date_only;
      if (!groups[dateString]) {
        groups[dateString] = [];
      }
      groups[dateString].push(procedure);
      return groups;
    }, {}) || {}; // Ensure default value if procedures is undefined or null

  return (
    <Grid container justifyContent={"center"}>
      <Paper sx={{ padding: "15px", width: "100%", mt: 2 }}>
        <Grid item xs={12}>
          <Typography
            variant="h5"
            fontWeight={"bold"}
            align="center"
            sx={{ padding: 2, borderBottom: "5px solid #007471" }}
          >
            {t("call_center.proceduresList")}
          </Typography>
          <Grid container justifyContent={"space-between"} spacing={3} mt={1}>
            <Grid item xs={12} sm={10} md={4}>
              <TextField
                fullWidth
                type="search"
                size="small"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                sx={{
                  width: { xs: "100%", sm: "80%", md: "30vw" },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "4px",
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  "aria-label": t("call_center.search"),
                }}
                placeholder={t("call_center.search")}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Grid container justifyContent={"space-between"} spacing={3}>
                <Grid item xs={6} md={4}>
                  <TextField
                    fullWidth
                    type="date"
                    size="small"
                    label={t("call_center.filterByDate")}
                    value={date}
                    onChange={(e) => {
                      setDate(e.target.value);
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item xs={6} md={4}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label={t("call_center.filterByType")}
                    value={type}
                    onChange={(e) => {
                      setType(e.target.value);
                    }}
                  >
                    {isLoadingType && (
                      <Grid
                        container
                        padding={"10px"}
                        justifyContent={"center"}
                      >
                        <CircularProgress size={20} />
                      </Grid>
                    )}
                    {isErrorType && (
                      <Typography color={"red"} variant="h6">
                        {t("call_center.fieldLoad")}
                      </Typography>
                    )}
                    <MenuItem value="">{t("call_center.all")}</MenuItem>
                    {typesData?.map((typeItem) => (
                      <MenuItem value={typeItem.id} key={typeItem.id}>
                        {typeItem?.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label={t("call_center.filterByStatus")}
                    value={status}
                    onChange={(e) => {
                      setStatus(e.target.value);
                    }}
                  >
                    <MenuItem value={1}>{t("call_center.done")}</MenuItem>
                    <MenuItem value={2}>{t("call_center.cancel")}</MenuItem>
                    <MenuItem value={3}>{t("call_center.followUP")}</MenuItem>
                    <MenuItem value={4}>{t("call_center.inProgress")}</MenuItem>
                    <MenuItem value={5}>{t("call_center.dontWant")}</MenuItem>
                    <MenuItem value={6}>{t("call_center.nextDate")}</MenuItem>
                    <MenuItem value={7}>{t("call_center.pending")}</MenuItem>
                    <MenuItem value={8}>{t("call_center.fixed")}</MenuItem>
                    <MenuItem value={9}>{t("call_center.flexible")}</MenuItem>
                    <MenuItem value={10}>{t("call_center.noAnswer")}</MenuItem>
                    <MenuItem value={11}>
                      {t("call_center.information")}
                    </MenuItem>
                    <MenuItem value={12}>
                      {t("call_center.comingSoon")}
                    </MenuItem>
                    <MenuItem value={13}>{t("call_center.block")}</MenuItem>
                  </TextField>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <TableContainer sx={{ marginTop: 2, overflowY: "auto" }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headCellStyle}>
                    {t("call_center.id")}
                  </TableCell>
                  <TableCell sx={headCellStyle}>
                    {t("call_center.patient")}
                  </TableCell>
                  <TableCell sx={headCellStyle}>
                    {t("call_center.contact")}
                  </TableCell>
                  <TableCell sx={headCellStyle}>
                    {t("call_center.procedureType")}
                  </TableCell>
                  <TableCell sx={headCellStyle}>
                    {t("call_center.date")}
                  </TableCell>
                  <TableCell sx={headCellStyle}>
                    {t("call_center.period")}
                  </TableCell>
                  <TableCell sx={headCellStyle}>
                    {t("call_center.doctor")}
                  </TableCell>
                  <TableCell sx={headCellStyle}>
                    {t("call_center.status")}
                  </TableCell>
                  <TableCell sx={headCellStyle}>
                    {t("call_center.zone")}
                  </TableCell>
                  <TableCell sx={headCellStyle}>
                    {t("call_center.lab")}
                  </TableCell>
                  <TableCell sx={headCellStyle}>
                    {t("call_center.totalPrice")}
                  </TableCell>
                  <TableCell sx={headCellStyle}>
                    {t("call_center.advance")}
                  </TableCell>
                  <TableCell sx={headCellStyle}>
                    {t("call_center.nextVisitDate")}
                  </TableCell>
                  <TableCell sx={headCellStyle}>
                    {t("call_center.action")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.keys(groupedProcedures).map((date) => (
                  <React.Fragment key={date}>
                    <TableRow>
                      <TableCell
                        colSpan={12}
                        sx={{
                          fontWeight: "bold",
                          textAlign: "center",
                          backgroundColor: "#f0f0f0",
                        }}
                      >
                        {date}
                      </TableCell>
                    </TableRow>
                    {groupedProcedures[date].map((procedure) => (
                      <TableRow
                        key={procedure.id}
                        style={getRowBackgroundColor(procedure)} // Apply color based on date or status
                      >
                        <TableCell sx={cellStyle}>{procedure.id}</TableCell>
                        <TableCell
                          sx={cellStyle}
                          style={{ fontWeight: "bold" }}
                        >
                          {procedure.patient_name}
                        </TableCell>
                        <TableCell
                          sx={cellStyle}
                          style={{ fontWeight: "bold" }}
                        >
                          {procedure?.patient_details?.phone}
                        </TableCell>
                        <TableCell
                          sx={cellStyle}
                          style={{ fontWeight: "bold" }}
                        >
                          {procedure.display_procedure_type}
                        </TableCell>
                        <TableCell sx={cellStyle}>
                          {procedure.procedure_date
                            ? FormatDateTime(procedure.procedure_date)
                            : t("call_center.noDate")}
                        </TableCell>
                        <TableCell
                          sx={cellStyle}
                          style={{ fontWeight: "bold" }}
                        >
                          {procedure.display_period}
                        </TableCell>
                        <TableCell sx={cellStyle}>
                          {procedure.display_doctor?.first_name || ""}{" "}
                          {procedure.display_doctor?.last_name || ""}
                        </TableCell>
                        <TableCell sx={cellStyle}>
                          <Chip
                            label={procedure?.status_name}
                            sx={{
                              fontWeight: "bold",
                              fontSize: "0.7rem",
                              color: "blue",
                            }}
                          />
                        </TableCell>
                        <TableCell sx={cellStyle}>
                          {procedure.zone || "-"}
                        </TableCell>
                        <TableCell sx={cellStyle}>
                          {procedure.display_lab &&
                          procedure.display_lab.length > 0 ? (
                            procedure.display_lab.map((lab, labIndex) => (
                              <Chip
                                key={labIndex}
                                label={lab}
                                size="small"
                                sx={{ mr: 0.5 }}
                              />
                            ))
                          ) : (
                            <Chip
                              label={t("call_center.noLabData")}
                              size="small"
                            />
                          )}
                        </TableCell>
                        <TableCell
                          sx={cellStyle}
                          style={{ fontWeight: "bold" }}
                        >
                          {procedure.total_cost}{" "}
                          {procedure.currency === 1
                            ? "؋"
                            : procedure.currency === 2
                            ? "$"
                            : procedure.currency === 3
                            ? "€"
                            : ""}
                        </TableCell>
                        <TableCell sx={cellStyle}>
                          {procedure?.advance || ""}{" "}
                          {procedure.advance_currency === 1
                            ? "؋"
                            : procedure.advance_currency === 2
                            ? "$"
                            : procedure.advance_currency === 3
                            ? "€"
                            : ""}
                        </TableCell>
                        <TableCell sx={cellStyle}>
                          {procedure.next_visit_date
                            ? FormatDateTime(procedure.next_visit_date)
                            : t("call_center.noDate")}
                        </TableCell>
                        <TableCell sx={cellStyle}>
                          <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => {
                              setProcedureId(procedure.id);
                              handleOpenDialog();
                            }}
                            sx={{
                              textTransform: "capitalize",
                              fontWeight: "bold",
                              mx: 0.5,
                              p: "2px 6px",
                            }}
                          >
                            {t("call_center.edit")}
                          </Button>
                          {user?.user?.isAdmin && (
                            <Button
                              variant="outlined"
                              color="error"
                              onClick={() => {
                                handleDelete(procedure.id);
                              }}
                              sx={{
                                textTransform: "capitalize",
                                fontWeight: "bold",
                                mx: 0.5,
                                p: "2px 6px",
                              }}
                            >
                              {t("call_center.delete")}
                            </Button>
                          )}
                          <Button
                            variant="contained"
                            color="error"
                            size="small"
                            sx={{ textTransform: "capitalize" }}
                            onClick={() => {
                              navigate("/reception/collect-beauty-fees/add", {
                                state: {
                                  phone: procedure?.patient_details?.phone,
                                },
                              });
                            }}
                          >
                            {t("call_center.print", "Print")}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
        {/* Pagination Controls */}
        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mt={2}
            mb={2}
          >
            <Box display="flex" alignItems="center">
              <Typography variant="caption">
                {t("call_center.rowsPerPage")}:{" "}
              </Typography>
              <FormControl
                sx={{
                  ml: 2,
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      border: "none",
                    },
                  },
                }}
                size="small"
              >
                <Select
                  value={rowsPerPage}
                  onChange={handleRowsPerPageChange}
                  displayEmpty
                  inputProps={{ "aria-label": t("call_center.rowsPerPage") }}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value={500}>500</MenuItem>
                  <MenuItem value={1000}>1000</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Pagination
              count={Math.ceil(totalProcedure / rowsPerPage)}
              page={page}
              onChange={handlePageChange}
              variant="outlined"
              color="primary"
              size="small"
            />
          </Box>
        </Grid>
      </Paper>
      <ConfirmDialog />
      <Dialog maxWidth="xl" open={open} onClose={handleOpenDialog}>
        <DialogContent>
          {description ? (
            <Typography variant="body">{description}</Typography>
          ) : (
            <EditProcedure id={procedureId} onClose={handleOpenDialog} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleOpenDialog}>{t("call_center.close")}</Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default ProcedureList;
